/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * SoonKi Min (skmin@rsupport.com)
 *
 * @description
 *
 * @created at Wed Jun 21 2023
 **/

import { PropsOf, useTheme } from "@emotion/react";
import { debounce } from "lodash-es";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { EventHandler, useEventHandler } from "@rfice/eventhandler";
import { _T } from "@rfice/language";
import { useFileUpload, useResizeWithDrag } from "@/frameworks/libraries/hooks";
import { Button, Column, Divider, Icon, Row, Typography } from "@/frameworks/designStuff/components";
import { EventID, ZIndex } from "@/frameworks/core/commons";
import { useChatMessageActions, useDMRoomState, useSpaceChannelState, useSpaceRoomState } from "@/frameworks/core/chat";
import { useMiniChatPopupManager, useSidebarActions } from "@/frameworks/core/hooks";
import { Dragger, Maybe, SwitchCase } from "@/frameworks/libraries/components";
import { UnDraggable } from "@/frameworks/libraries/components/Dragger";

import { VoiceCallLayer, MessageInput, MessageList } from "../../../Common";
import { useMiniChatPopup, Header, S, withOutsideViewportInChat } from "./private";
import { UnauthorizedChannel } from "../Commons";
import { DeletedChannel } from "../Commons/DeletedChannel";
import { MessageSearchInConversation, MessageSearchResult } from "./components";
import ThreadMessageList from "../../../Common/ThreadMessageList/ThreadMessageList";

interface MiniChatPopupProps {
	id: string;
	type: "DM" | "Space";
	spaceID?: string;
	spaceName?: string;
	size?: {
		width?: number;
		height?: number;
	};
	zIndex?: number;
	threadID?: string;
	onPointerDown?: (ev: React.PointerEvent) => void;
	onPointerUp?: (ev: React.PointerEvent) => void;

	// pass by withOutsideViewportInChat
	chatLayoutRef?: React.RefObject<HTMLDivElement>;
}

export const MiniChatPopupSizeConfig = {
	MIN_HEIGHT: 520,
	MIN_WIDTH: 360,
	DEFAULT_HEIGHT: 640,
	DEFAULT_WIDTH: 400
};

export const MiniChatPopupComponent = React.memo(function MiniChatPopup(props: MiniChatPopupProps) {
	// 외부 클릭에 대한 내부 클릭에 대한 불일치로 인한 오류 방지를 위한 플립플롭 변수
	const headerOptionFlipFlop = useRef(false);
	//드래그 시작 좌표 저장
	const dragStartCoordinate = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
	const { colors } = useTheme();
	const { messageReaded, threadReaded } = useChatMessageActions();
	const { inputRef, drag, removeAll, reset } = useFileUpload({ id: props.id, multiple: true });
	const { ref: resizeRef } = useResizeWithDrag<HTMLDivElement>();
	const sidebarActions = useSidebarActions();
	const { id, type, spaceID, threadID } = props;
	const { miniChatPopupActions } = useMiniChatPopupManager();
	const focusRef = useRef(false);

	const space = useSpaceChannelState(props.spaceID ?? "");
	const spaceRoom = useSpaceRoomState({ spaceID: props.spaceID ?? "", roomID: props.id });
	const dmRoom = useDMRoomState(props.id);

	const [isShowMessageSearchResult, setIsShowMessageSearchResult] = useState(false);
	const [searchText, setSearchText] = useState<string>("");
	const [isSearchInputOpened, setIsSearchInputOpened] = useState(false);

	const channelData = spaceRoom ?? dmRoom;

	const channelUserList = useMemo(() => {
		return (
			channelData?.users.map((user) => ({
				name: user.name,
				src: user.profileImagePath,
				userID: user.id
			})) || []
		);
	}, [channelData?.users]);

	const { topBarColor, handleChannelOption, handleHide, handleClose } = useMiniChatPopup({
		id,
		spaceID: props.spaceID,
		groupID: space?.groupId || ""
	});

	const handlePointerUpHeader = useCallback(
		(e: React.PointerEvent) => {
			if (dragStartCoordinate.current.x === e.clientX && dragStartCoordinate.current.y === e.clientY) {
				if (!headerOptionFlipFlop.current) {
					headerOptionFlipFlop.current = true;
					if (channelData) {
						handleChannelOption(e, channelData);
					}
				} else {
					headerOptionFlipFlop.current = false;
				}
			} else {
				headerOptionFlipFlop.current = false;
			}
		},
		[channelData]
	);

	const handlePointerDown = useCallback(
		(ev: React.PointerEvent) => {
			miniChatPopupActions.focus(props.id);
			sidebarActions.setFocus(undefined);
			dragStartCoordinate.current = { x: ev.clientX, y: ev.clientY };
		},
		[props.id]
	);

	const { onDrop, ...rest } = drag.listeners;
	const handleDrop = (ev: React.DragEvent<HTMLLabelElement>) => {
		EventHandler.emit(EventID.DROP_CHAT_FILES, {
			channelID: props.id,
			fileList: [...ev.dataTransfer.files],
			variant: "miniPopup"
		});
		onDrop(ev);
		removeAll();
		reset();
	};

	const handleShowRecentMessage = useCallback(
		debounce((message) => {
			if (message.isMe) return;
			if (miniChatPopupActions.getCurrentFocus()?.id !== props.id) return;
			if (message.roomMessageId === channelData?.messageLastReadId) return;
			messageReaded({ channelID: id, messageID: message.roomMessageId });
		}, 1000),
		[channelData?.messageLastReadId]
	);

	const handleShowRecentThreadMessage = useCallback(
		debounce((message) => {
			threadReaded({ roomId: id, threadMessageId: message.roomMessageId });
		}, 1000),
		[channelData?.messageLastReadId]
	);

	useEventHandler(
		EventID.TOGGLE_OPEN_SEARCH_ON_CONVERSATION_INPUT,
		({ roomId: targetRoomId }: { roomId: string }) => {
			if (targetRoomId !== id) return;
			setIsSearchInputOpened((prev) => !prev);
			setIsShowMessageSearchResult(Boolean(searchText && !isSearchInputOpened));
		},
		[id, searchText, isSearchInputOpened]
	);

	useEventHandler(
		EventID.OPEN_SEARCH_RESULT_ON_MINI_CHAT,
		({
			searchValue,
			isOpenSearchResult,
			roomId: targetRoomId
		}: {
			searchValue: string;
			isOpenSearchResult: boolean;
			roomId: string;
		}) => {
			if (targetRoomId !== id) return;
			if (isOpenSearchResult) {
				setIsShowMessageSearchResult(Boolean(searchValue?.trim()));
				if (!searchValue || searchValue.trim().length === 0) return;
				setSearchText(searchValue);
			} else {
				setSearchText("");
				setIsShowMessageSearchResult(false);
			}
		}
	);

	const handleOnClearSearchInput = () => {
		setIsShowMessageSearchResult(false);
		setSearchText("");
	};

	const handleSearchCompleted = () => {
		setIsSearchInputOpened(false);
		setSearchText("");
		setIsShowMessageSearchResult(false);
	};

	const handleCloseSearchUI = () => {
		setIsSearchInputOpened(false);
		setIsShowMessageSearchResult(false);
	};

	if (!channelData) return;
	return (
		<Dragger
			zIndex={props?.zIndex ?? 4000}
			className={`draggableMiniChat ${id.slice(0, 10)}`}
			ref={resizeRef}
			css={{
				minWidth: MiniChatPopupSizeConfig.MIN_WIDTH,
				minHeight: MiniChatPopupSizeConfig.MIN_HEIGHT,
				width: props?.size?.width ?? MiniChatPopupSizeConfig.DEFAULT_WIDTH,
				height: props?.size?.height ?? MiniChatPopupSizeConfig.DEFAULT_HEIGHT
			}}
			{...props}
		>
			<S.MiniPopupWrapper
				autoFocus
				ref={props.chatLayoutRef}
				tabIndex={0}
				onPointerDown={handlePointerDown}
				onDrop={handleDrop}
				{...rest}
				onFocus={() => {
					focusRef.current = true;
					if (channelData.hasNewMessage) {
						messageReaded({ channelID: id, messageID: channelData.lastSentMessage?.id || "" });
						threadReaded({ roomId: id, threadMessageId: channelData.lastSentMessage?.id || "" });
					}
				}}
				onBlur={() => {
					focusRef.current = false;
				}}
				css={{ width: props?.size?.width, height: props?.size?.height }}
			>
				{drag.isDragging && <IsDragging />}
				<input type="file" id="file-upload" multiple={true} ref={inputRef} style={{ display: "none" }} />
				<Header
					roomID={id}
					spaceID={spaceID}
					threadID={threadID}
					type={type}
					background={topBarColor}
					privateMode={channelData.private || false}
					userList={channelUserList}
					onPointerUp={handlePointerUpHeader}
					onHide={() => {
						handleHide(type, type === "DM" ? { users: channelUserList } : { spaceName: space?.spaceName }, {
							width: resizeRef.current?.offsetWidth,
							height: resizeRef.current?.offsetHeight
						});
					}}
					onClose={handleClose}
					{...(() => {
						if (type === "DM") {
							return {
								title: channelData.roomName || ""
							};
							// type === "Space"
						} else {
							if (space?.spaceType === "OPEN_WORLD" || space?.spaceType === "GROUP_OPEN_WORLD") {
								return {
									title: space?.spaceName || "",
									subTitle: _T("오픈월드")
								};
							} else {
								return {
									title: channelData.roomName || "",
									subTitle: space?.spaceName || ""
								};
							}
						}
					})()}
				/>
				<Maybe test={isSearchInputOpened && id}>
					<Row
						verticalAlign="center"
						gap={4}
						style={{
							width: "100%",
							padding: "4px 4px 4px 12px",
							backgroundColor:
								isShowMessageSearchResult && Boolean(searchText.trim())
									? colors.accent_10_100
									: colors.neutral_10_100
						}}
					>
						<MessageSearchInConversation
							searchedText={searchText}
							onClear={handleOnClearSearchInput}
							roomId={id}
						/>
						<Row verticalAlign="center">
							<Button.Icon
								shape="round"
								name="Close24"
								size="medium"
								variant="text"
								onClick={handleCloseSearchUI}
							/>
						</Row>
					</Row>
				</Maybe>
				<VoiceCallLayer chatSize="mini" roomID={channelData.roomId} spaceID={space?.spaceId} />
				<Maybe
					test={channelData.roomJoined}
					fallback={<UnauthorizedChannel createdBy={channelData.createdBy} />}
				>
					<SwitchCase
						condition={(() => {
							if (channelData.deleted && channelData.roomJoined) return "deletedChannel";
							if (isShowMessageSearchResult && Boolean(searchText.trim())) return "searchResult";
							//  undefined === 'defaultCase' === "messageList";
							return undefined;
						})()}
						cases={{
							deletedChannel: <DeletedChannel roomID={channelData.roomId} />,
							searchResult: (
								<MessageSearchResult
									onComplete={handleSearchCompleted}
									searchText={searchText}
									roomID={id}
								/>
							)
						}}
						defaultCase={
							<ChatContentWithLayout
								id={id}
								type={type}
								threadID={props.threadID}
								channelData={channelData}
								channelUserList={channelUserList}
								handleShowRecentMessage={handleShowRecentMessage}
								handleShowRecentThreadMessage={handleShowRecentThreadMessage}
							/>
						}
					/>
				</Maybe>
			</S.MiniPopupWrapper>
		</Dragger>
	);
});

const MiniChatPopupWithOutsideViewport = withOutsideViewportInChat(MiniChatPopupComponent);
export default MiniChatPopupWithOutsideViewport;

function ChatContent({
	id,
	type,
	threadID,
	channelData,
	channelUserList,
	handleShowRecentMessage,
	handleShowRecentThreadMessage,
	uiType
}: {
	id: string;
	type: "DM" | "Space" | "CONFERENCE";
	threadID?: string;
	channelData: SwaggerModels.RoomResponseVO;
	channelUserList: { name: string; src: string | undefined; userID: string }[];
	handleShowRecentMessage: (message: any) => void;
	handleShowRecentThreadMessage: (message: any) => void;
	uiType?: ChatContentLayoutUIType;
}) {
	const theme = useTheme();
	const dividerRef = useRef<HTMLDivElement>(null);
	const [dragWidth, setDragWidth] = useState<{ width: string }[]>([{ width: "100%" }, { width: "100%" }]);

	useEffect(() => {
		const divider = dividerRef.current;
		if (!divider || uiType !== "split" || !threadID) return;

		const handleMouseMove = (e: PointerEvent) => {
			e.preventDefault();
			const parentRect = divider.parentElement?.getBoundingClientRect();

			if (parentRect && uiType === "split" && threadID) {
				const x = e.clientX;
				const parentWidth = parentRect.width;
				const childWidth = x - parentRect.left;
				setDragWidth(() => [{ width: `${childWidth}px` }, { width: `${parentWidth - childWidth}px` }]);
			}
		};

		const handleMouseUp = () => {
			document.removeEventListener("pointermove", handleMouseMove);
			document.removeEventListener("pointerup", handleMouseUp);
		};

		const handleMouseDown = (e: MouseEvent) => {
			e.preventDefault();
			e.stopPropagation();
			document.addEventListener("pointermove", handleMouseMove);
			document.addEventListener("pointerup", handleMouseUp);
		};

		divider.addEventListener("mousedown", handleMouseDown);

		return () => {
			divider.removeEventListener("mousedown", handleMouseDown);
			document.removeEventListener("pointermove", handleMouseMove);
			document.removeEventListener("pointerup", handleMouseUp);
		};
	}, [uiType, threadID]);

	useEffect(() => {
		//스레드기능을 사용하지 않으면 두개의 컬럼의 너비를 100%로 변경
		if (!threadID) {
			setDragWidth(() => [{ width: "100%" }, { width: "100%" }]);
		}
	}, [threadID]);

	return (
		<Row css={{ width: "100%", height: "100%", position: "relative" }}>
			<Column
				css={{
					width: dragWidth[0].width,
					height: "100%",
					background: theme.colors.neutral_00_84,
					borderRadius: "12px",
					minWidth: 360,
					// 겹쳤을때 bg-opacity 존재로 미관상 문제가 있어 숨김처리
					...(uiType === "collapse" &&
						threadID && {
							visibility: "hidden"
						})
				}}
			>
				<MessageList
					variant="miniPopup"
					roomID={id}
					isMyRoom={channelData.myRoom}
					spaceID={channelData.spaceId}
					threadID={threadID}
					notice={channelData.notice}
					voteNotice={channelData.voteNotice}
					myRoomID={channelData.myRoomId}
					onShowRecentMessage={handleShowRecentMessage}
				/>
				<MessageInput
					id={id}
					type={type}
					disabled={channelData.deleted && channelData.roomJoined}
					userList={channelUserList}
					variant="miniPopup"
					myRoomID={channelData.myRoomId}
					spaceID={channelData.spaceId}
					isMyRoom={channelData.myRoom}
				/>
			</Column>
			{/* 분리형이며, 스레드가 있는 경우에만 Divider 출력 */}
			{uiType === "split" && threadID && (
				<UnDraggable>
					<Divider
						type="vertical"
						ref={dividerRef}
						css={{
							zIndex: ZIndex.CHAT_POPUP + 1
						}}
					/>
				</UnDraggable>
			)}
			<Maybe test={threadID}>
				<Column
					css={{
						minWidth: 360,
						width: dragWidth[1].width,
						height: "100%",
						background: theme.colors.neutral_00_84,
						borderRadius: "12px",
						...(uiType === "collapse" && {
							position: "absolute",
							top: 0,
							left: 0,
							width: "100%"
						}),
						...(uiType === "split" && {
							boxShadow: "0px 8px 12px 0px #0000001F, 0px 0px 4px 0px #0000000A"
						})
					}}
				>
					<ThreadMessageList
						variant="miniPopup"
						roomID={id}
						isMyRoom={channelData.myRoom}
						spaceID={channelData.spaceId}
						threadID={threadID!}
						notice={channelData.notice}
						voteNotice={channelData.voteNotice}
						myRoomID={channelData.myRoomId}
						onShowRecentMessage={handleShowRecentThreadMessage}
					/>
					<MessageInput
						id={id}
						type={type}
						disabled={channelData.deleted && channelData.roomJoined}
						userList={channelUserList}
						variant="miniPopup"
						myRoomID={channelData.myRoomId}
						spaceID={channelData.spaceId}
						isMyRoom={channelData.myRoom}
						threadID={threadID}
					/>
				</Column>
			</Maybe>
		</Row>
	);
}

const ChatContentWithLayout = withChatContentLayout(ChatContent);

function IsDragging() {
	const theme = useTheme();
	return (
		<Row
			verticalAlign="center"
			horizonAlign="center"
			gap={12}
			css={{
				position: "absolute",
				top: 0,
				left: 0,
				width: "100%",
				height: "100%",
				background: theme.colors.secondary_80_16,
				backdropFilter: "blur(14px)",
				zIndex: ZIndex.CHAT_POPUP + 1,
				borderRadius: "12px"
			}}
		>
			<Typography typoColor="neutral_80_100" typoSize="Title1">
				파일 업로드
			</Typography>
			<Icon name="Upload24" fill={theme.colors.neutral_80_100} size={24} />
		</Row>
	);
}

const ChatContentLayoutUIType = {
	split: "split",
	collapse: "collapse"
};
type ChatContentLayoutUIType = keyof typeof ChatContentLayoutUIType;

function withChatContentLayout<T>(Component: React.ComponentType<T>) {
	return function WithChatContentLayout(props: PropsOf<typeof Component>) {
		const [uiType, setUiType] = useState<ChatContentLayoutUIType>("split");
		const handleResize = (entry: ResizeObserverEntry) => {
			const { width } = entry.contentRect;
			if (width < 800) {
				setUiType("collapse");
			} else {
				setUiType("split");
			}
		};

		const resizeTargetRef = useRef<HTMLDivElement>(null);

		useEffect(() => {
			const observer = new ResizeObserver((entries) => {
				entries.forEach((item) => {
					handleResize(item);
				});
			});

			if (resizeTargetRef.current) {
				observer.observe(resizeTargetRef.current);
			}
			return () => {
				if (resizeTargetRef.current) {
					observer.unobserve(resizeTargetRef.current);
				}
				observer.disconnect();
			};
		}, []);

		return (
			<Row
				ref={resizeTargetRef}
				style={{
					width: "100%",
					height: `calc(100% - ${resizeTargetRef.current?.previousElementSibling?.clientHeight}px)`
				}}
			>
				<Component {...props} uiType={uiType} />
			</Row>
		);
	};
}
